import { useEffect, useState } from "react";
import { Accordion, Alert, Button, Col, FormGroup, FormLabel, Row, Tabs, Tab, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InputPassword from "../../../../components/Forms/Element/InputPassword";
import FieldValidationError from "../../../../components/Forms/Element/FieldValidationError";
import CustomSelect from "../../../../components/Forms/Element/CustomSelect";
import AsteriskRequired from "../../../../components/Common/AsteriskRequired";
import FormSection from "../../../../components/Common/Form/FormSection";
import HintTooltip from "../../../../components/Common/HintTooltip";
import { errorAlert, warnAlertYesOrNo } from "../../../../components/alert/defaultAlert";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { formatPhoneNumber } from "../../../../../utils/FormatUtils";
import WhatsappEmbeddedSignupButton from "./WhatsappEmbeddedSignupButton";
import "./ChatbotSettings.css";

const WHATSAPP_SETTINGS_BASE_PATH = "/integration/whatsapp/settings";
const templateStatusTranslations = {
    NOT_SUBMITTED: 'chatbot_billing_template_status_not_submitted',
    PENDING: 'chatbot_billing_template_status_pending',
    APPROVED: 'chatbot_billing_template_status_approved',
    REJECTED: 'chatbot_billing_template_status_rejected',
    PAUSED: 'chatbot_billing_template_status_paused',
    DISABLED: 'chatbot_billing_template_status_disabled',
    IN_APPEAL: 'chatbot_billing_template_status_pending',
    NOT_FOUND: 'chatbot_billing_template_status_not_found'
};
const templateGroupOrder = [
    { key: 'appointments', titleKey: 'whatsapp_templates_group_appointments' },
    { key: 'feedback', titleKey: 'whatsapp_templates_group_feedback' },
    { key: 'billing', titleKey: 'whatsapp_templates_group_billing' },
    { key: 'birthdays', titleKey: 'whatsapp_templates_group_birthdays' }
];

const formatWhatsappPhoneNumber = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    const brazilianNumber = digits.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
    if (brazilianNumber) {
        return `+55 (${brazilianNumber[1]}) ${brazilianNumber[2]}-${brazilianNumber[3]}`;
    }
    return formatPhoneNumber(value);
};

const isTemplateEligibleForReview = (model) => ['NOT_SUBMITTED', 'NOT_FOUND'].includes(
    String(model?.status || 'NOT_SUBMITTED').toUpperCase()
);

const ChatbotSettingsForm = (props) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        setStatus,
        formik,
        onSubmit,
        entityData,
        onWhatsAppConnected,
        onWhatsAppDisconnected
    } = props;

    const [disconnectingWhatsapp, setDisconnectingWhatsapp] = useState(false);
    const [templateModels, setTemplateModels] = useState([]);
    const [templateModelsBusy, setTemplateModelsBusy] = useState(false);
    const [templateModelAction, setTemplateModelAction] = useState(null);
    const [templateModelsError, setTemplateModelsError] = useState('');
    const [templateBulkResult, setTemplateBulkResult] = useState(null);
    const [templateTestRecipients, setTemplateTestRecipients] = useState({});
    const [templateTestStates, setTemplateTestStates] = useState({});
    const [activeTemplateGroup, setActiveTemplateGroup] = useState('appointments');
    const [whatsappBillingIssue, setWhatsappBillingIssue] = useState(null);
    const [showChatbotToken, setShowChatbotToken] = useState(false);
    const [chatbotConnectionTest, setChatbotConnectionTest] = useState(null);
    const [metaSetupInfo, setMetaSetupInfo] = useState({
        verifyToken: "",
        webhookPath: "/whatsapp/webhook",
        embeddedSignupAppId: "",
        embeddedSignupConfigId: "",
        graphApiVersion: "",
        embeddedSignupReady: false
    });
    const [loadingMetaSetupInfo, setLoadingMetaSetupInfo] = useState(true);
    const [registrationPin, setRegistrationPin] = useState("");
    // null means Meta's current state has not been confirmed yet; don't offer a duplicate action during that window.
    const [numberRegistered, setNumberRegistered] = useState(null);
    const [webhookSubscribed, setWebhookSubscribed] = useState(null);
    const [setupHealthLoading, setSetupHealthLoading] = useState(false);
    const [setupHealthError, setSetupHealthError] = useState("");
    const [setupHealthRefresh, setSetupHealthRefresh] = useState(0);
    const [embeddedSignupResult, setEmbeddedSignupResult] = useState(null);
    const [metaActionLoading, setMetaActionLoading] = useState({
        register: false,
        subscribe: false
    });
    const [metaActionResult, setMetaActionResult] = useState(null);
    const [activeStep, setActiveStep] = useState("whatsapp");
    const isWhatsappConnected = Boolean(entityData?.whatsapp?.apiTokenConfigured);
    const chatbotCredentialSaved = Boolean(entityData?.chatbot?.apiToken);
    const chatbotSettingsDirty = (formik.values.chatbotApiToken || '') !== (entityData?.chatbot?.apiToken || '')
        || formik.values.model !== (entityData?.chatbot?.model || 'gpt-4o-mini');
    const registrationPinValid = /^\d{6}$/.test(registrationPin);

    const resolveTemplateModelsError = (error, fallbackKey) => {
        const message = error?.response?.data?.message || '';
        if (message.toLowerCase().includes('application has been deleted')) {
            return t('whatsapp_template_app_deleted_error');
        }
        return message || t(fallbackKey);
    };

    const loadTemplateModels = async (sync = false) => {
        setTemplateModelsBusy(true);
        setTemplateModelsError('');
        try {
            const response = sync
                ? await axiosInstance.post(Api.parameter.whatsapp_template_models_sync)
                : await axiosInstance.get(Api.parameter.whatsapp_template_models);
            setTemplateModels(response.data?.data || []);
        } catch (error) {
            setTemplateModelsError(resolveTemplateModelsError(error, 'chatbot_billing_template_load_failed'));
        } finally {
            setTemplateModelsBusy(false);
        }
    };

    const submitTemplateModel = async (key) => {
        setTemplateModelAction(key);
        setTemplateModelsError('');
        try {
            const response = await axiosInstance.post(`${Api.parameter.whatsapp_template_models}/${key}/submit`);
            setTemplateModels((current) => current.map((item) => item.key === key ? response.data : item));
        } catch (error) {
            setTemplateModelsError(resolveTemplateModelsError(error, 'chatbot_billing_template_submit_failed'));
        } finally {
            setTemplateModelAction(null);
        }
    };

    const submitAllEligibleTemplates = async () => {
        const eligibleModels = templateModels.filter(isTemplateEligibleForReview);
        if (!eligibleModels.length) return;

        setTemplateBulkResult(null);
        setTemplateModelsError('');
        const failures = [];
        let submittedCount = 0;
        // Envia um por vez para registrar o resultado individual e preservar as aprovações parciais.
        for (const model of eligibleModels) {
            setTemplateModelAction(`bulk:${model.key}`);
            try {
                const response = await axiosInstance.post(`${Api.parameter.whatsapp_template_models}/${model.key}/submit`);
                setTemplateModels((current) => current.map((item) => item.key === model.key ? response.data : item));
                submittedCount += 1;
            } catch (error) {
                failures.push({
                    label: model.label || model.key,
                    message: resolveTemplateModelsError(error, 'chatbot_billing_template_submit_failed')
                });
            }
        }
        setTemplateBulkResult({ submittedCount, failures, total: eligibleModels.length });
        setTemplateModelAction(null);
    };

    const sendTemplateTest = async (model) => {
        let nationalNumber = String(templateTestRecipients[model.key] || '').replace(/\D/g, '');
        // O campo já exibe +55; removemos um DDI colado junto para não duplicá-lo no envio.
        if (nationalNumber.startsWith('55') && [12, 13].includes(nationalNumber.length)) {
            nationalNumber = nationalNumber.slice(2);
        }
        if (!nationalNumber) {
            setTemplateTestStates((current) => ({ ...current, [model.key]: { type: 'error', message: t('whatsapp_template_test_number_required') } }));
            return;
        }
        setTemplateModelAction(`test:${model.key}`);
        setTemplateTestStates((current) => ({ ...current, [model.key]: { type: 'loading', message: '' } }));
        try {
            const response = await axiosInstance.post(`${Api.parameter.whatsapp_template_models}/${model.key}/test`, { toNumber: `55${nationalNumber}` });
            setTemplateTestStates((current) => ({ ...current, [model.key]: {
                type: 'success',
                message: response.data?.message || t('whatsapp_template_test_sent'),
                messageId: response.data?.messageId,
                deliveryPending: true
            } }));
            if (response.data?.messageId) pollTemplateDelivery(model.key, response.data.messageId);
        } catch (error) {
            const message = error?.response?.data?.message || t('whatsapp_template_test_failed');
            setTemplateTestStates((current) => ({ ...current, [model.key]: { type: 'error', message } }));
        } finally {
            setTemplateModelAction(null);
        }
    };

    const pollTemplateDelivery = async (templateKey, messageId, attempt = 0) => {
        if (attempt >= 12) {
            setTemplateTestStates((current) => ({ ...current, [templateKey]: {
                ...current[templateKey], deliveryPending: false,
                deliveryNote: t('whatsapp_template_delivery_pending')
            } }));
            return;
        }
        window.setTimeout(async () => {
            try {
                const response = await axiosInstance.get(`${Api.parameter.whatsapp_template_message_status}/${encodeURIComponent(messageId)}/delivery-status`);
                const delivery = response.data || {};
                if (delivery.status === 'failed') {
                    setTemplateTestStates((current) => ({ ...current, [templateKey]: {
                        ...current[templateKey], type: 'error', deliveryPending: false,
                        message: t('whatsapp_template_delivery_failed'), delivery
                    } }));
                    if (String(delivery.code) === '131042') setWhatsappBillingIssue(delivery);
                    return;
                }
                if (['delivered', 'read'].includes(String(delivery.status).toLowerCase())) {
                    setTemplateTestStates((current) => ({ ...current, [templateKey]: {
                        ...current[templateKey], type: 'success', deliveryPending: false,
                        deliveryStatus: delivery.status
                    } }));
                    return;
                }
            } catch (error) {
                // Falha temporária na consulta não invalida o envio; o webhook segue sendo a fonte do recibo.
            }
            pollTemplateDelivery(templateKey, messageId, attempt + 1);
        }, 5000);
    };

    useEffect(() => {
        if (!isWhatsappConnected) {
            setWhatsappBillingIssue(null);
            return;
        }
        axiosInstance.get(Api.parameter.whatsapp_billing_issue)
            .then((response) => setWhatsappBillingIssue(response.data?.active ? response.data : null))
            .catch(() => setWhatsappBillingIssue(null));
    }, [isWhatsappConnected, entityData?.whatsapp?.phoneNumberId]);

    useEffect(() => {
        if (isWhatsappConnected) {
            // O status local pode estar desatualizado; sincronizar com a Meta evita oferecer envio para análise a um modelo já aprovado.
            loadTemplateModels(true);
        } else {
            setTemplateModels([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWhatsappConnected, entityData?.whatsapp?.businessAccountId]);

    useEffect(() => {
        const phoneNumberId = entityData?.whatsapp?.phoneNumberId;
        const businessAccountId = entityData?.whatsapp?.businessAccountId;
        if (!isWhatsappConnected || !phoneNumberId || !businessAccountId) {
            setNumberRegistered(null);
            setWebhookSubscribed(null);
            setSetupHealthError("");
            return undefined;
        }

        let mounted = true;
        setNumberRegistered(null);
        setWebhookSubscribed(null);
        setSetupHealthLoading(true);
        setSetupHealthError("");

        axiosInstance.post(Api.parameter.whatsapp_account_health, { phoneNumberId, businessAccountId })
            .then((response) => {
                if (!mounted) return;
                const phoneStatus = String(response.data?.phone?.status || "").toUpperCase();
                setNumberRegistered(phoneStatus ? phoneStatus === "CONNECTED" : null);
                const webhookStatus = response.data?.webhook?.subscribed;
                setWebhookSubscribed(typeof webhookStatus === "boolean" ? webhookStatus : null);

                const errors = response.data?.errors || [];
                if (!phoneStatus || typeof webhookStatus !== "boolean"
                    || errors.some((item) => item.section === "phone" || item.section === "webhook")) {
                    setSetupHealthError(t("whatsapp_setup_status_check_failed", {
                        defaultValue: "Não foi possível confirmar o registro do número e a inscrição do webhook na Meta. Tente verificar novamente."
                    }));
                }
            })
            .catch(() => {
                if (mounted) {
                    setSetupHealthError(t("whatsapp_setup_status_check_failed", {
                        defaultValue: "Não foi possível confirmar o registro do número e a inscrição do webhook na Meta. Tente verificar novamente."
                    }));
                }
            })
            .finally(() => {
                if (mounted) setSetupHealthLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [isWhatsappConnected, entityData?.whatsapp?.phoneNumberId, entityData?.whatsapp?.businessAccountId, setupHealthRefresh, t]);

    useEffect(() => {
        const pathStep = location.pathname.match(/\/settings\/([^/]+)\/?$/)?.[1];
        const stepByPath = {
            "configure-whatsapp": "whatsapp",
            "configure-chatbot": "chatbot"
        };

        if (!pathStep && location.pathname.replace(/\/$/, '') === WHATSAPP_SETTINGS_BASE_PATH) {
            navigate(`${WHATSAPP_SETTINGS_BASE_PATH}/configure-whatsapp`, { replace: true });
            return;
        }

        const resolvedStep = stepByPath[pathStep];
        if (!resolvedStep) {
            navigate(`${WHATSAPP_SETTINGS_BASE_PATH}/configure-whatsapp`, { replace: true });
            return;
        }

        setActiveStep(resolvedStep);
    }, [location.pathname, navigate]);

    useEffect(() => {
        if (entityData) {
            // Chatbot params
            formik.setFieldValue('chatbotApiToken', entityData.chatbot?.apiToken || '');
            formik.setFieldValue('model', entityData.chatbot?.model || 'gpt-4o-mini');
            
            // Status: verifica se whatsapp existe e tem status, converte para boolean
            const whatsappStatus = entityData.whatsapp?.status;
            if (whatsappStatus !== undefined && whatsappStatus !== null) {
                // Status pode ser: "ACTIVE", "INACTIVE", true, false, ou 1, 0
                let isActive = false;
                if (typeof whatsappStatus === 'string') {
                    isActive = whatsappStatus.toUpperCase() === 'ACTIVE';
                } else if (typeof whatsappStatus === 'boolean') {
                    isActive = whatsappStatus;
                } else if (typeof whatsappStatus === 'number') {
                    isActive = whatsappStatus === 1;
                }
                setStatus(isActive);
            }
        }
        // eslint-disable-next-line
    }, [entityData]);

    useEffect(() => {
        let mounted = true;

        const loadMetaSetupInfo = async () => {
            setLoadingMetaSetupInfo(true);
            try {
                const response = await axiosInstance.get(Api.parameter.whatsapp_meta_setup_info);
                if (mounted && response?.data) {
                    setMetaSetupInfo({
                        verifyToken: response.data.verifyToken || "",
                        webhookPath: response.data.webhookPath || "/whatsapp/webhook",
                        embeddedSignupAppId: response.data.embeddedSignupAppId || "",
                        embeddedSignupConfigId: response.data.embeddedSignupConfigId || "",
                        graphApiVersion: response.data.graphApiVersion || "",
                        embeddedSignupReady: Boolean(response.data.embeddedSignupReady)
                    });
                }
            } catch (error) {
                if (mounted) {
                    setMetaSetupInfo({
                        verifyToken: "",
                        webhookPath: "/whatsapp/webhook",
                        embeddedSignupAppId: "",
                        embeddedSignupConfigId: "",
                        graphApiVersion: "",
                        embeddedSignupReady: false
                    });
                }
            } finally {
                if (mounted) {
                    setLoadingMetaSetupInfo(false);
                }
            }
        };

        loadMetaSetupInfo();

        return () => {
            mounted = false;
        };
    }, []);

    const handleDisconnectWhatsapp = () => {
        warnAlertYesOrNo(t("whatsapp_disconnect_confirm"), async () => {
            setDisconnectingWhatsapp(true);
            try {
                const response = await axiosInstance.delete(Api.parameter.whatsapp_disconnect);
                // A API pode concluir a remoção local mesmo quando a Meta não aceita o token para remover o webhook.
                setNumberRegistered(false);
                setWebhookSubscribed(false);
                setRegistrationPin("");
                setMetaActionResult(null);
                await onWhatsAppDisconnected?.(response?.data);
            } catch (error) {
                errorAlert(error?.response?.data?.message || t("whatsapp_disconnect_failed"));
            } finally {
                setDisconnectingWhatsapp(false);
            }
        });
    };

    const handleRegisterNumber = async () => {
        setMetaActionLoading((current) => ({ ...current, register: true }));
        setMetaActionResult(null);

        try {
            const response = await axiosInstance.post(Api.parameter.whatsapp_register_number, {
                phoneNumberId: entityData?.whatsapp?.phoneNumberId,
                pin: registrationPin
            });

            setMetaActionResult({
                success: true,
                title: t("whatsapp_number_registered_title"),
                message: response.data?.message || "Número registrado com sucesso na Meta.",
                data: response.data?.data || null
            });
            setNumberRegistered(true);
            setRegistrationPin("");
        } catch (error) {
            const apiMessage = error?.response?.data?.message || "";
            // O código 133005 é uma recusa recuperável da Meta: o PIN não confere, então mantenha o campo para correção.
            const pinMismatch = /133005|pin mismatch|pin.*(?:incorreto|inválido|nao confere|não confere)/i.test(apiMessage);
            setMetaActionResult({
                success: false,
                kind: pinMismatch ? "pin-mismatch" : "error",
                title: t(pinMismatch ? "whatsapp_number_pin_mismatch_title" : "whatsapp_number_register_failed_title", {
                    defaultValue: pinMismatch ? "O PIN não confere" : "Não foi possível registrar o número"
                }),
                message: t(pinMismatch ? "whatsapp_number_pin_mismatch_message" : "whatsapp_number_register_failed_message", {
                    defaultValue: pinMismatch
                        ? "A Meta recusou este PIN. Confira os 6 dígitos e tente novamente. O número ainda não foi ativado."
                        : "Ocorreu um erro ao solicitar o registro à Meta. Tente novamente; se persistir, consulte os detalhes técnicos."
                }),
                technicalDetails: error?.response?.data || (apiMessage ? { message: apiMessage } : null)
            });
        } finally {
            setMetaActionLoading((current) => ({ ...current, register: false }));
        }
    };

    const handleSubscribeApp = async () => {
        setMetaActionLoading((current) => ({ ...current, subscribe: true }));
        setMetaActionResult(null);

        try {
            const response = await axiosInstance.post(Api.parameter.whatsapp_subscribe_app, {
                businessAccountId: entityData?.whatsapp?.businessAccountId
            });

            setMetaActionResult({
                success: true,
                title: t("whatsapp_webhook_subscribed_title"),
                message: response.data?.message || "Webhook inscrito com sucesso na Meta.",
                data: response.data?.data || null
            });
            setWebhookSubscribed(true);
        } catch (error) {
            setMetaActionResult({
                success: false,
                title: "Falha na inscrição",
                message: error?.response?.data?.message || "Não foi possível inscrever o app no webhook da Meta.",
                data: error?.response?.data?.data || null
            });
        } finally {
            setMetaActionLoading((current) => ({ ...current, subscribe: false }));
        }
    };

    // GPT-3.5 e GPT-4 Turbo saíram da lista: são fracos com as ferramentas de agendamento. Os modelos de
    // raciocínio (GPT-5) passaram a funcionar porque o backend ajusta os parâmetros conforme o modelo.
    const baseModelOptions = [
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Recomendado)' },
        { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
        { value: 'gpt-4o', label: 'GPT-4o' },
        { value: 'gpt-4.1', label: 'GPT-4.1' },
        { value: 'gpt-5-mini', label: 'GPT-5 Mini' }
    ];
    // Um modelo antigo já salvo continua visível para a empresa trocar quando quiser, sem campo em branco.
    const modelOptions = formik.values.model && !baseModelOptions.some((option) => option.value === formik.values.model)
        ? [...baseModelOptions, { value: formik.values.model, label: `${formik.values.model} (${t('chatbot_model_current')})` }]
        : baseModelOptions;

    const renderSetupStep = (number, title, description) => (
        <span className="chatbot-settings__step">
            <span className="chatbot-settings__step-number" aria-hidden="true">{number}</span>
            <span className="chatbot-settings__step-copy">
                <span className="chatbot-settings__step-label" aria-hidden="true">Etapa {String(number).padStart(2, '0')}</span>
                <strong>{title}</strong>
                <small>{description}</small>
            </span>
            <i className="fas fa-arrow-right chatbot-settings__step-arrow" aria-hidden="true"></i>
        </span>
    );

    // Group by product purpose; Meta's category (UTILITY, etc.) remains separate metadata on each template.
    const templateModelsByGroup = templateModels.reduce((groups, model) => {
        const knownGroup = templateGroupOrder.some((group) => group.key === model.group);
        const groupKey = knownGroup ? model.group : 'other';
        groups[groupKey] = [...(groups[groupKey] || []), model];
        return groups;
    }, {});
    const groupedTemplateModels = [
        ...templateGroupOrder,
        { key: 'other', titleKey: 'whatsapp_templates_group_other' }
    ].filter((group) => templateModelsByGroup[group.key]?.length)
        .map((group) => ({ ...group, models: templateModelsByGroup[group.key] }));
    const eligibleTemplateCount = templateModels.filter(isTemplateEligibleForReview).length;

    const testChatbotConnection = async () => {
        const apiToken = formik.values.chatbotApiToken?.trim() || entityData?.chatbot?.apiToken || '';
        if (!apiToken) {
            setChatbotConnectionTest({ type: 'error', message: t('chatbot_test_connection_missing_key') });
            return;
        }

        setChatbotConnectionTest({ type: 'loading', message: '' });
        try {
            const response = await axiosInstance.post(`${Api.parameter.chatbot}/test-connection`, {
                apiToken,
                model: formik.values.model
            });
            setChatbotConnectionTest({ type: 'success', message: response.data?.message || t('chatbot_test_connection_success') });
        } catch (error) {
            setChatbotConnectionTest({
                type: 'error',
                message: error?.response?.data?.message || t('chatbot_test_connection_failed')
            });
        }
    };

    return (
        <Tabs
            activeKey={activeStep}
            onSelect={(key) => {
                if (!key) {
                    return;
                }

                const stepPath = {
                    whatsapp: "configure-whatsapp",
                    chatbot: "configure-chatbot"
                }[key] || "configure-whatsapp";
                setActiveStep(key);
                navigate(`${WHATSAPP_SETTINGS_BASE_PATH}/${stepPath}`);
            }}
            className="chatbot-settings__tabs"
            mountOnEnter
            unmountOnExit={false}
        >
            {/* Tab: Configurações do WhatsApp */}
            <Tab eventKey="whatsapp" title={
                renderSetupStep(1, t('whatsapp_setup_step_credentials'), t('whatsapp_setup_step_credentials_help'))
            }>
                <div className="pt-3">
                    <div className={`chatbot-settings__whatsapp-connection ${isWhatsappConnected ? "is-connected" : "is-disconnected"}`} role="status" aria-live="polite">
                        <div className="chatbot-settings__whatsapp-connection-icon" aria-hidden="true">
                            <i className="fa-brands fa-whatsapp" />
                        </div>
                        <div className="chatbot-settings__whatsapp-connection-copy">
                            <div className="chatbot-settings__whatsapp-connection-heading">
                                <h6>{isWhatsappConnected ? t("whatsapp_embedded_signup_connected_title") : t("whatsapp_embedded_signup_title")}</h6>
                                {isWhatsappConnected && (
                                    <span className="chatbot-settings__whatsapp-connection-badge">
                                        <i className="fas fa-circle-check" aria-hidden="true" /> {t("whatsapp_connection_status_connected")}
                                    </span>
                                )}
                            </div>
                            <p>{isWhatsappConnected ? t("whatsapp_connection_connected_description") : t("chatbot_settings_whatsapp_info")}</p>
                            {isWhatsappConnected && (
                                <div className="chatbot-settings__whatsapp-connection-number">
                                    <span>{t("whatsapp_connection_number_label")}</span>
                                    <strong>{entityData?.whatsapp?.phoneNumber
                                        ? formatWhatsappPhoneNumber(entityData.whatsapp.phoneNumber)
                                        : t("whatsapp_meta_health_not_informed")}</strong>
                                </div>
                            )}
                        </div>
                        {isWhatsappConnected && (
                            <Button
                                type="button"
                                variant="danger"
                                className="chatbot-settings__whatsapp-disconnect"
                                onClick={handleDisconnectWhatsapp}
                                disabled={disconnectingWhatsapp}
                            >
                                {disconnectingWhatsapp
                                    ? <><Spinner animation="border" size="sm" /> {t("whatsapp_disconnecting")}</>
                                    : <><i className="fas fa-unlink me-2" aria-hidden="true" />{t("whatsapp_disconnect")}</>}
                            </Button>
                        )}
                    </div>

                    {!loadingMetaSetupInfo && !metaSetupInfo.embeddedSignupReady && (
                        /* Aviso separado do botão para manter a mensagem na largura total da seção. */
                        <Alert variant="warning" className="mb-3">
                            {t("whatsapp_embedded_signup_not_configured")}
                        </Alert>
                    )}

                    {!isWhatsappConnected && (
                        <Alert variant="info" className="mb-3">
                            {t("whatsapp_embedded_signup_registration_notice")}
                        </Alert>
                    )}

                    {!isWhatsappConnected && (
                        <Row className="chatbot-settings__signup-controls">
                            <Col md={7} className="mb-3">
                                <div className="mt-3">
                                    <WhatsappEmbeddedSignupButton
                                        metaSetupInfo={metaSetupInfo}
                                        onResult={setEmbeddedSignupResult}
                                        onConnected={async (result) => {
                                            if (result?.activationComplete) {
                                                setNumberRegistered(true);
                                            }
                                            await onWhatsAppConnected?.(result);
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                    {embeddedSignupResult && (
                        <Alert variant={embeddedSignupResult.variant} className="mb-3 w-100">
                            {embeddedSignupResult.message}
                        </Alert>
                    )}

                    {isWhatsappConnected && (
                        <Row>
                            <Col md={12} className="mb-3">
                                <FormSection title="Ativação do número" description={t("whatsapp_embedded_signup_activation_help")} icon="fas fa-sim-card">
                                    {setupHealthLoading && (
                                        <Alert variant="info" className="mb-3 d-flex align-items-center gap-2">
                                            <Spinner animation="border" size="sm" />
                                            <span>{t("whatsapp_setup_status_checking", { defaultValue: "Verificando o status do número e do webhook na Meta…" })}</span>
                                        </Alert>
                                    )}
                                    {setupHealthError && (
                                        <Alert variant="warning" className="mb-3" role="status">
                                            <div>{setupHealthError}</div>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => setSetupHealthRefresh((current) => current + 1)}
                                                disabled={setupHealthLoading}
                                            >
                                                {t("whatsapp_setup_status_retry", { defaultValue: "Verificar novamente" })}
                                            </Button>
                                        </Alert>
                                    )}
                                    {numberRegistered === true ? (
                                        <Alert variant="success" className="mb-3">
                                            <i className="fas fa-circle-check me-2" aria-hidden="true" />
                                            <strong>{t("whatsapp_number_ready_title")}</strong>
                                            <div className="mt-1">{t("whatsapp_number_ready_description")}</div>
                                        </Alert>
                                    ) : numberRegistered === false ? (
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <FormGroup>
                                                    <FormLabel>{t("whatsapp_embedded_signup_pin_label")}</FormLabel>
                                                    <InputPassword
                                                        inputMode="numeric"
                                                        autoComplete="one-time-code"
                                                        placeholder={t("whatsapp_embedded_signup_pin_placeholder")}
                                                        value={registrationPin}
                                                        onChange={(event) => setRegistrationPin(event.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    />
                                                    <small className="form-text text-muted">{t("whatsapp_embedded_signup_pin_help")}</small>
                                                </FormGroup>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="whatsapp-setup-summary">
                                                    <div>
                                                        <span>Phone Number ID</span>
                                                        <strong>{entityData?.whatsapp?.phoneNumberId || "Não informado"}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Business Account ID</span>
                                                        <strong>{entityData?.whatsapp?.businessAccountId || "Não informado"}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    ) : null}

                                    <div className="d-flex gap-3 flex-wrap">
                                        {numberRegistered === false && (
                                            <button
                                                type="button"
                                                className="btn btn-success d-flex align-items-center gap-2"
                                                onClick={handleRegisterNumber}
                                                disabled={metaActionLoading.register || !entityData?.whatsapp?.phoneNumberId || !registrationPinValid}
                                            >
                                                {metaActionLoading.register ? (
                                                    <><Spinner animation="border" size="sm" /> {t("whatsapp_number_registering")}</>
                                                ) : (
                                                    <><i className="fas fa-sim-card" aria-hidden="true" />{t("whatsapp_number_register_action")}</>
                                                )}
                                            </button>
                                        )}

                                        {webhookSubscribed === null ? (
                                            <span className="text-muted d-inline-flex align-items-center gap-2 px-2" role="status">
                                                {setupHealthLoading && <Spinner animation="border" size="sm" />}
                                                <span>{setupHealthLoading
                                                    ? t("whatsapp_setup_status_checking_short", { defaultValue: "Verificando status…" })
                                                    : t("whatsapp_setup_status_unknown", { defaultValue: "Status do webhook não confirmado" })}</span>
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-primary d-flex align-items-center gap-2"
                                                onClick={handleSubscribeApp}
                                                disabled={metaActionLoading.subscribe || webhookSubscribed || numberRegistered !== true || !entityData?.whatsapp?.businessAccountId}
                                            >
                                                {metaActionLoading.subscribe ? (
                                                    <><Spinner animation="border" size="sm" /> {t("whatsapp_webhook_subscribing")}</>
                                                ) : webhookSubscribed ? (
                                                    <><i className="fas fa-circle-check" aria-hidden="true" />{t("whatsapp_webhook_subscribed_action")}</>
                                                ) : (
                                                    <><i className="fas fa-link" aria-hidden="true" />{t("whatsapp_webhook_subscribe_action")}</>
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {numberRegistered === false && (
                                        <div className="small text-muted mt-3">
                                            <i className="fas fa-circle-info me-2" aria-hidden="true" />{t("whatsapp_number_pending_hint")}
                                        </div>
                                    )}
                                    {numberRegistered === true && webhookSubscribed === false && (
                                        <div className="small text-muted mt-3">
                                            <i className="fas fa-circle-info me-2" aria-hidden="true" />{t("whatsapp_webhook_pending_hint")}
                                        </div>
                                    )}

                                    {metaActionResult && !metaActionResult.success && (
                                        <div className={`whatsapp-registration-feedback ${metaActionResult.kind === "pin-mismatch" ? "is-warning" : "is-error"} mt-4`} role="alert" aria-live="assertive">
                                            <i className={`fas ${metaActionResult.kind === "pin-mismatch" ? "fa-triangle-exclamation" : "fa-circle-exclamation"} mt-1`} aria-hidden="true" />
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold mb-1">{metaActionResult.title}</div>
                                                <div>{metaActionResult.message}</div>
                                                {metaActionResult.technicalDetails && (
                                                    <details className="mt-3">
                                                        <summary>{t("whatsapp_meta_technical_details", { defaultValue: "Ver detalhes técnicos" })}</summary>
                                                        <pre className="whatsapp-setup-response mb-0 mt-2">
                                                            {JSON.stringify(metaActionResult.technicalDetails, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {metaActionResult?.success && metaActionResult.data && (
                                        <details className="mt-3">
                                            <summary>{t("whatsapp_meta_technical_details", { defaultValue: "Ver detalhes técnicos" })}</summary>
                                            <pre className="whatsapp-setup-response mb-0 mt-2">
                                                {JSON.stringify(metaActionResult.data, null, 2)}
                                            </pre>
                                        </details>
                                    )}
                                </FormSection>
                            </Col>
                        </Row>
                    )}

                    {isWhatsappConnected && (
                        <FormSection title={t('whatsapp_templates_title')} icon="fa-regular fa-message" compact className="mb-4">
                            <p className="chatbot-settings__templates-description">{t('whatsapp_templates_description')}</p>
                            {templateModels.length > 0 && (
                                <div className="chatbot-settings__template-bulk-action">
                                    <div className="chatbot-settings__template-bulk-info">
                                        <span className="chatbot-settings__template-bulk-icon"><i className="fa-solid fa-paper-plane" aria-hidden="true" /></span>
                                        <div className="chatbot-settings__template-bulk-copy">
                                            <strong>{t('whatsapp_templates_bulk_title')}</strong>
                                            <small>{t('whatsapp_templates_bulk_help')}</small>
                                        </div>
                                        <span className={`chatbot-settings__template-bulk-count${eligibleTemplateCount === 0 ? ' is-empty' : ''}`}>
                                            {t('whatsapp_templates_bulk_count', { count: eligibleTemplateCount })}
                                        </span>
                                    </div>
                                    <Button type="button" variant="primary" onClick={submitAllEligibleTemplates} disabled={Boolean(templateModelAction) || templateModelsBusy || eligibleTemplateCount === 0}>
                                        {String(templateModelAction || '').startsWith('bulk:')
                                            ? <><Spinner animation="border" size="sm" /> {t('whatsapp_templates_bulk_submitting')}</>
                                            : eligibleTemplateCount > 0
                                                ? t('whatsapp_templates_bulk_submit')
                                                : t('whatsapp_templates_bulk_none')}
                                    </Button>
                                </div>
                            )}
                            {templateBulkResult && (
                                <Alert variant={templateBulkResult.failures.length ? 'warning' : 'success'} className="mt-3" role="status">
                                    {templateBulkResult.failures.length === 0
                                        ? t('whatsapp_templates_bulk_success', { count: templateBulkResult.submittedCount })
                                        : t('whatsapp_templates_bulk_partial', { submitted: templateBulkResult.submittedCount, total: templateBulkResult.total })}
                                    {templateBulkResult.failures.length > 0 && (
                                        <ul className="mb-0 mt-2">
                                            {templateBulkResult.failures.map((failure) => <li key={failure.label}><strong>{failure.label}:</strong> {failure.message}</li>)}
                                        </ul>
                                    )}
                                </Alert>
                            )}
                            {templateModelsBusy && templateModels.length === 0 ? (
                                <div className="chatbot-settings__templates-loading"><Spinner animation="border" size="sm" /> {t('loading')}</div>
                            ) : (
                                <div className="chatbot-settings__template-groups">
                                    <Accordion activeKey={activeTemplateGroup} onSelect={(eventKey) => setActiveTemplateGroup(eventKey || '')}>
                                        {groupedTemplateModels.map((group) => {
                                            const approvedCount = group.models.filter((model) => model.status === 'APPROVED').length;
                                            // Modelo aprovado com conteúdo divergente do catálogo também pede atenção: ele não é usado nos envios.
                                            const attentionCount = group.models.filter((model) => ['PENDING', 'IN_APPEAL', 'REJECTED', 'PAUSED', 'DISABLED'].includes(model.status) || Boolean(model.contentMismatch)).length;
                                            return (
                                            <Accordion.Item eventKey={group.key} key={group.key} className="chatbot-settings__template-accordion-item">
                                                <Accordion.Header>
                                                    <div className="chatbot-settings__template-accordion-heading">
                                                        <strong>{t(group.titleKey)}</strong>
                                                        <span className="chatbot-settings__template-count">{group.models.length}</span>
                                                        <span className="chatbot-settings__template-group-summary">
                                                            <span className="is-approved"><i className="fa-solid fa-circle-check" aria-hidden="true" /> {approvedCount}</span>
                                                            {attentionCount > 0 && <span className="needs-attention"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {attentionCount}</span>}
                                                        </span>
                                                        <span className="chatbot-settings__template-accordion-toggle" aria-hidden="true">
                                                            {activeTemplateGroup === group.key ? '−' : '+'}
                                                        </span>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                            <div className="chatbot-settings__templates-list">
                                                {group.models.map((model) => {
                                                    const modelStatus = String(model.status || 'NOT_SUBMITTED').toUpperCase();
                                                    const canSubmit = isTemplateEligibleForReview(model);
                                                    const testState = templateTestStates[model.key];
                                                    const rejectedReason = model.rejectedReason?.trim();
                                                    return (
                                                        <article className="chatbot-settings__template-card" key={model.key}>
                                                            <div className="chatbot-settings__template-heading">
                                                                <div>
                                                                    <h6>{model.label || model.key}</h6>
                                                                    <small className="chatbot-settings__template-meta">
                                                                        <span><strong>{t('whatsapp_template_meta_name')}:</strong> {model.name}</span>
                                                                        <span><strong>{t('whatsapp_template_meta_language')}:</strong> {model.language}</span>
                                                                        <span><strong>{t('whatsapp_template_meta_category')}:</strong> {model.category}</span>
                                                                    </small>
                                                                </div>
                                                                <div className="chatbot-settings__template-heading-actions">
                                                                    <span className={`chatbot-settings__template-status is-${modelStatus.toLowerCase()}`}>
                                                                        <i className="fa-solid fa-circle" aria-hidden="true" />
                                                                        {t(templateStatusTranslations[modelStatus] || 'chatbot_billing_template_status_pending')}
                                                                    </span>
                                                                    <Button type="button" variant="light" size="sm" className="chatbot-settings__template-refresh-icon" onClick={() => loadTemplateModels(true)} disabled={templateModelsBusy || Boolean(templateModelAction)} aria-label={t('chatbot_billing_template_refresh')} title={t('chatbot_billing_template_refresh')}>
                                                                        {templateModelsBusy ? <Spinner animation="border" size="sm" /> : <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            {model.body && <blockquote>{model.body}</blockquote>}
                                                            {model.buttons?.length > 0 && (
                                                                <small className="text-muted d-block mt-2">
                                                                    <strong>{t('whatsapp_template_buttons_label')}:</strong> {model.buttons.join(', ')}
                                                                </small>
                                                            )}
                                                            {model.variables?.length > 0 && (
                                                                <small className="text-muted d-block mt-2">
                                                                    <strong>{t('whatsapp_template_variables_label')}:</strong> {model.variables.join(', ')}
                                                                </small>
                                                            )}
                                                            {rejectedReason && rejectedReason.toUpperCase() !== 'NONE' && (
                                                                <small className="text-danger d-block mt-2">{rejectedReason}</small>
                                                            )}
                                                            {model.contentMismatch && (
                                                                <small className="text-danger d-block mt-2">
                                                                    {t('whatsapp_template_content_mismatch', { detail: model.contentMismatch })}
                                                                </small>
                                                            )}
                                                            <div className="chatbot-settings__template-actions">
                                                                {modelStatus === 'APPROVED' && (
                                                                    <div className="chatbot-settings__template-test">
                                                                        <label className="form-label chatbot-settings__template-test-label" htmlFor={`template-test-${model.key}`}>
                                                                            <i className="fa-solid fa-mobile-screen-button" aria-hidden="true" />
                                                                            {t('whatsapp_template_test_number')}
                                                                        </label>
                                                                        <div className="chatbot-settings__template-test-row">
                                                                            <span className="chatbot-settings__template-country-code" aria-hidden="true">+55</span>
                                                                            <input
                                                                                id={`template-test-${model.key}`}
                                                                                type="tel"
                                                                                className="form-control"
                                                                                value={templateTestRecipients[model.key] || ''}
                                                                                onChange={(event) => setTemplateTestRecipients((current) => ({ ...current, [model.key]: event.target.value }))}
                                                                                placeholder={t('whatsapp_template_test_number_placeholder')}
                                                                                aria-label={t('whatsapp_template_test_number_input')}
                                                                                inputMode="tel"
                                                                                autoComplete="tel"
                                                                                disabled={templateModelAction === `test:${model.key}`}
                                                                            />
                                                                            <Button type="button" variant="success" className="chatbot-settings__template-test-send" onClick={() => sendTemplateTest(model)} disabled={Boolean(templateModelAction) || templateModelsBusy} aria-label={t('whatsapp_template_test_send')} title={t('whatsapp_template_test_send')}>
                                                                                {templateModelAction === `test:${model.key}` ? <Spinner animation="border" size="sm" /> : <i className="fa-solid fa-paper-plane" aria-hidden="true" />}
                                                                            </Button>
                                                                        </div>
                                                                        <small className="chatbot-settings__template-test-help"><i className="fa-solid fa-circle-info" aria-hidden="true" />{t('whatsapp_template_test_help')}</small>
                                                                        {testState?.type !== 'loading' && testState?.message && <small className={`d-block mt-2 ${testState.type === 'success' ? 'text-success' : 'text-danger'}`} role="status" aria-live="polite">{testState.message}</small>}
                                                                        {testState?.deliveryPending && <small className="d-block mt-1 text-muted" role="status">{t('whatsapp_template_delivery_checking')}</small>}
                                                                        {testState?.deliveryStatus && <small className="d-block mt-1 text-success">{t('whatsapp_template_delivery_status')}: {t(`whatsapp_delivery_status_${testState.deliveryStatus}`, { defaultValue: testState.deliveryStatus })}</small>}
                                                                        {testState?.deliveryNote && <small className="d-block mt-1 text-muted">{testState.deliveryNote}</small>}
                                                                        {testState?.delivery?.details && <Alert variant="warning" className="mt-2 mb-0 py-2">
                                                                            {testState.delivery.title && <strong className="d-block">{testState.delivery.title}</strong>}
                                                                            {testState.delivery.message && <div className="mt-1">{testState.delivery.message}</div>}
                                                                            {testState.delivery.resolutionMessage && <div className="mt-1">{testState.delivery.resolutionMessage}</div>}
                                                                            <div>{testState.delivery.details}</div>
                                                                            {testState.delivery.code && <small className="d-block mt-1">{t('whatsapp_delivery_error_code')}: {testState.delivery.code}</small>}
                                                                            {testState.delivery.helpUrl && <a href={testState.delivery.helpUrl} target="_blank" rel="noopener noreferrer" className="d-inline-block mt-2">{t('whatsapp_billing_issue_action')}</a>}
                                                                        </Alert>}
                                                                        {testState?.type === 'success' && testState.messageId && <small className="d-block mt-1 text-muted">{t('whatsapp_template_test_message_id')}: <code>{testState.messageId}</code></small>}
                                                                    </div>
                                                                )}
                                                                <div className="chatbot-settings__template-controls">
                                                                    {canSubmit && (
                                                                        <Button type="button" variant="primary" onClick={() => submitTemplateModel(model.key)} disabled={Boolean(templateModelAction) || templateModelsBusy}>
                                                                            {templateModelAction === model.key ? <><Spinner animation="border" size="sm" /> {t('loading')}</> : t('chatbot_billing_template_submit')}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        );})}
                                    </Accordion>
                                    {!templateModelsBusy && templateModels.length === 0 && <p className="text-muted mb-0">{t('whatsapp_templates_empty')}</p>}
                                </div>
                            )}
                            {whatsappBillingIssue && (
                                <Alert variant="warning" className="mt-3" role="status">
                                    <strong>{t('whatsapp_billing_issue_title')}</strong>
                                    <div className="mt-1">{whatsappBillingIssue.resolutionMessage || t('whatsapp_billing_issue_description')}</div>
                                    {whatsappBillingIssue.message && <div className="mt-2">{whatsappBillingIssue.message}</div>}
                                    {whatsappBillingIssue.details && <div className="mt-2">{whatsappBillingIssue.details}</div>}
                                    {whatsappBillingIssue.code && <small className="d-block mt-2">{t('whatsapp_delivery_error_code')}: {whatsappBillingIssue.code}</small>}
                                    {whatsappBillingIssue.helpUrl && <Button as="a" href={whatsappBillingIssue.helpUrl} target="_blank" rel="noopener noreferrer" variant="outline-dark" size="sm" className="mt-3">{t('whatsapp_billing_issue_action')}</Button>}
                                </Alert>
                            )}
                            {templateModelsError && <small className="text-danger d-block mt-3">{templateModelsError}</small>}
                        </FormSection>
                    )}

                    <section className="chatbot-settings__meta-help" aria-labelledby="whatsapp-meta-help-title">
                        <div className="chatbot-settings__meta-help-copy">
                            <span className="chatbot-settings__meta-help-icon" aria-hidden="true">
                                <i className="fa-brands fa-meta" />
                            </span>
                            <div>
                                <h5 id="whatsapp-meta-help-title">{t("whatsapp_meta_help_title")}</h5>
                                <p>{t("whatsapp_meta_help_description")}</p>
                            </div>
                        </div>
                        <div className="chatbot-settings__meta-help-actions">
                            <a href="https://business.facebook.com/select" target="_blank" rel="noopener noreferrer">
                                <i className="fa-solid fa-briefcase" aria-hidden="true" />
                                {t("whatsapp_meta_help_manager")}
                                <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                            </a>
                            <a href="https://business.facebook.com/business-support-home/" target="_blank" rel="noopener noreferrer">
                                <i className="fa-regular fa-life-ring" aria-hidden="true" />
                                {t("whatsapp_meta_help_support")}
                                <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                            </a>
                        </div>
                    </section>

                </div>
            </Tab>

            {/* Tab: Configurações do Chatbot IA */}
            <Tab eventKey="chatbot" title={
                renderSetupStep(2, t('whatsapp_setup_step_chatbot'), t('whatsapp_setup_step_chatbot_help'))
            }>
                <form onSubmit={onSubmit}>
                <div className="pt-3">
                    <section className="chatbot-settings__ai-hero">
                        <div className="chatbot-settings__ai-hero-icon" aria-hidden="true">
                            <i className="fa-solid fa-brain" />
                        </div>
                        <div className="chatbot-settings__ai-hero-copy">
                            <span className="chatbot-settings__ai-eyebrow">{t('chatbot_ai_eyebrow')}</span>
                            <h2>{t('chatbot_ai_title')}</h2>
                            <p>{t('chatbot_settings_ai_info')}</p>
                            <div className="chatbot-settings__ai-badges">
                                <span className="chatbot-settings__ai-provider"><i className="fa-solid fa-cloud" aria-hidden="true" /> OpenAI</span>
                                <span className={`chatbot-settings__ai-status${chatbotConnectionTest?.type === 'success' ? ' is-tested' : chatbotSettingsDirty ? ' is-unsaved' : chatbotCredentialSaved ? ' is-saved' : ''}`}>
                                    <i className={`fa-solid ${chatbotConnectionTest?.type === 'success' ? 'fa-circle-check' : chatbotSettingsDirty ? 'fa-pen-to-square' : chatbotCredentialSaved ? 'fa-key' : 'fa-circle-minus'}`} aria-hidden="true" />
                                    {chatbotConnectionTest?.type === 'success'
                                        ? t('chatbot_ai_status_tested')
                                        : chatbotSettingsDirty
                                            ? t('chatbot_ai_status_unsaved')
                                            : chatbotCredentialSaved
                                            ? t('chatbot_ai_status_saved')
                                            : t('chatbot_ai_status_pending')}
                                </span>
                            </div>
                        </div>
                        <div className="chatbot-settings__ai-hero-decoration" aria-hidden="true"><i className="fa-solid fa-sparkles" /></div>
                    </section>

                    {/* Seção: Conexão com a IA */}
                    <FormSection
                        title={t('chatbot_ai_connection_title')}
                        description={t('chatbot_ai_connection_description')}
                        icon="fa-solid fa-sliders"
                        className="chatbot-settings__ai-config mb-4"
                    >
                            <Row>
                                <Col md={7} className="mb-3">
                                    <FormGroup>
                                        <FormLabel className="d-flex align-items-center">
                                            API Key <AsteriskRequired />
                                            <HintTooltip text={t('field_tips.chatbot_api_key')} />
                                        </FormLabel>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                name="chatbotApiToken"
                                                aria-label="OpenAI API Key"
                                                className="form-control input-default"
                                                placeholder="sk-..."
                                                onChange={(event) => {
                                                    formik.handleChange(event);
                                                    setChatbotConnectionTest(null);
                                                }}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.chatbotApiToken}
                                                type={showChatbotToken ? 'text' : 'password'}
                                                style={{ paddingRight: '2.5rem' }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 text-muted"
                                                onClick={() => setShowChatbotToken(v => !v)}
                                                tabIndex={-1}
                                                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', lineHeight: 1 }}
                                            >
                                                <i className={`fas ${showChatbotToken ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        <FieldValidationError error={formik.errors.chatbotApiToken} />
                                    </FormGroup>
                                </Col>
                                <Col md={5} className="mb-3">
                                    <FormGroup>
                                        <FormLabel className="d-flex align-items-center">
                                            {t('model')} <AsteriskRequired />
                                            <HintTooltip text={t('field_tips.chatbot_model')} />
                                        </FormLabel>
                                        <CustomSelect
                                            inputId="chatbot-model"
                                            options={modelOptions}
                                            value={modelOptions.find((option) => option.value === formik.values.model) || null}
                                            onChange={(option) => {
                                                formik.setFieldValue('model', option?.value || '');
                                                setChatbotConnectionTest(null);
                                            }}
                                            onBlur={() => formik.setFieldTouched('model', true)}
                                            isSearchable={false}
                                            placeholder={t('chatbot_model_select_placeholder')}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <div className="chatbot-settings__ai-test">
                                <div className="chatbot-settings__ai-test-copy">
                                    <span className="chatbot-settings__ai-test-icon" aria-hidden="true"><i className="fa-solid fa-plug" /></span>
                                    <div>
                                        <strong>{t('chatbot_ai_test_title')}</strong>
                                        <p>{t('chatbot_test_connection_help')}</p>
                                    </div>
                                </div>
                                <Button type="button" variant="primary" className="chatbot-settings__ai-test-button" onClick={testChatbotConnection} disabled={chatbotConnectionTest?.type === 'loading'}>
                                    {chatbotConnectionTest?.type === 'loading'
                                        ? <><Spinner size="sm" className="me-2" />{t('chatbot_test_connection_loading')}</>
                                        : <><i className="fa-solid fa-bolt me-2" aria-hidden="true" />{t('chatbot_test_connection_button')}</>}
                                </Button>
                            </div>
                            {chatbotConnectionTest && chatbotConnectionTest.type !== 'loading' && (
                                <Alert variant={chatbotConnectionTest.type === 'success' ? 'success' : 'danger'} className={`chatbot-settings__connection-result ${chatbotConnectionTest.type === 'success' ? 'is-success' : 'is-error'}`} role="status" aria-live="polite">
                                    <i className={`fas ${chatbotConnectionTest.type === 'success' ? 'fa-circle-check' : 'fa-triangle-exclamation'} me-2`} aria-hidden="true" />
                                    {chatbotConnectionTest.message}
                                </Alert>
                            )}
                            <section className="chatbot-settings__meta-help chatbot-settings__openai-help" aria-labelledby="openai-billing-help-title">
                                <div className="chatbot-settings__meta-help-copy">
                                    <span className="chatbot-settings__meta-help-icon" aria-hidden="true">
                                        <i className="fa-solid fa-wallet" />
                                    </span>
                                    <div>
                                        <h5 id="openai-billing-help-title">{t("chatbot_openai_billing_title")}</h5>
                                        <p>{t("chatbot_openai_billing_description")}</p>
                                    </div>
                                </div>
                                <div className="chatbot-settings__meta-help-actions">
                                    <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noopener noreferrer">
                                        <i className="fa-solid fa-credit-card" aria-hidden="true" />
                                        {t("chatbot_openai_billing_action")}
                                        <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                                    </a>
                                </div>
                            </section>
                            <div className="chatbot-settings__form-actions">
                                <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                                    {formik.isSubmitting ? (
                                        <><Spinner size="sm" className="me-2" />{t('saving')}...</>
                                    ) : (
                                        <><i className="fas fa-save me-2" aria-hidden="true" />{t('save')}</>
                                    )}
                                </Button>
                            </div>
                    </FormSection>

                </div>
                </form>
            </Tab>

        </Tabs>
    );
};

export default ChatbotSettingsForm;
